import React, { useState } from "react";

import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import { PencilAltIcon, TrashIcon } from "@heroicons/react/solid";
import { createColumnHelper } from "@tanstack/react-table";

import { IPerson } from "../../models/person/Person";
import { useDebounce } from "../../hooks/useDebounce";
import useUserContext from "../../contexts/UserContext";
import formatDateTime from "../../utils/formatDateTime";
import { Table } from "../../components/Shared/Table/Table";
import { peopleQueryKeys } from "../../data/people/queryKeys";
import { PageHeader } from "../../components/Shared/PageHeader";
import ActionButton from "../../components/Shared/ActionButton";
import { SearchInput } from "../../components/Shared/SearchInput";
import { ButtonInput } from "../../components/Shared/Inputs/ButtonInput";
import ActionButtonContainer from "../../components/Shared/ActionButtonContainer";
import { CreateUpdatePersonFlyout } from "../../components/Person/CreateUpdatePersonFlyout";
import { useMarketingGroups } from "../../data/marketingGroup/useMarketingGroups";
import { StyledSelectInput } from "../../components/Shared/StyledInputs/StyledSelectInput";
import { useForm } from "react-hook-form";
import ConfirmDelete from "../../components/Shared/ConfirmDelete";
import { usePeopleMutations } from "../../data/people/usePeopleMutations";
import { ImportPeopleModal } from "../../components/People/ImportPeopleModal";

const colHelper = createColumnHelper<IPerson>();

export default function People() {
  const navigate = useNavigate();
  const { marketingGroups } = useMarketingGroups();
  const { user: { agency } } = useUserContext();
  const { remove } = usePeopleMutations();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [createUpdatePersonOpen, setCreateUpdatePersonOpen] = useState(false);
  const [editPerson, setEditPerson] = useState<IPerson>();
  const [selectedGroup, setSelectedGroup] = useState<string>("-1");
  const [deletePersonId, setDeletePersonId] = useState<string>();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // this is all fake not actually used, just to make the select work for filtering
  const { register, watch, setValue, getValues } = useForm({
    defaultValues: {
      selectedGroup: "-1"
    }
  });

  const handleEditClick = (person: IPerson) => {
    setEditPerson(person);
    setCreateUpdatePersonOpen(true);
  }

  const handleDeleteClick = (personId: string) => {
    setDeletePersonId(personId);
    setConfirmDeleteOpen(true);
  }

  const handleConfirmDelete = () => {
    remove.mutate(deletePersonId!, {
      onSuccess: () => setConfirmDeleteOpen(false)
    });
  }

  const cols = [
    colHelper.display({
      header: "Name",
      cell: (info) => {
        if (info.row.original.firstName && info.row.original.lastname) {
          return <span>{`${info.row.original.firstName} ${info.row.original.lastname}`}</span>
        } else {
          return <span className="text-gray-400">N/A</span>
        }
      }
    }),
    colHelper.accessor("email", {
      header: "Email",
    }),
    colHelper.display({
      header: "Telephone",
      cell: (info) => (
        <div className="space-x-2">
          {info.row.original.telephone ?
            <span>{info.row.original.telephone}</span>
            : <span className="text-gray-400">N/A</span>}
          <span>/</span>
          {info.row.original.telephone2 ?
            <span>{info.row.original.telephone2}</span>
            : <span className="text-gray-400">N/A</span>}
        </div>
      )
    }),
    colHelper.display({
      header: "Source",
      cell: (info) => {
        const personSources = info.row.original.personSources;
        if (!personSources || personSources.length === 0 || !personSources[0].source) return "Unknown";

        return personSources.map(source => source.source).join(", ");
      }
    }),
    colHelper.accessor("created", {
      header: "Created On",
      cell: (info) => formatDateTime(
        info.getValue(),
        DateTime.DATETIME_SHORT
      )
    }),
    colHelper.accessor("updated", {
      header: "Updated On",
      cell: (info) => info.getValue() ? formatDateTime(
        info.getValue()!,
        DateTime.DATETIME_SHORT
      ) : <span className="text-gray-400">N/A</span>
    }),
    colHelper.accessor("unsubscribed", {
      header: "Subscribed",
      cell: (info) => info.getValue() ? "No" : "Yes"
    }),
    colHelper.display({
      header: "Actions",
      cell: (info) => (
        <ActionButtonContainer>
          <ActionButton onClick={() => handleEditClick(info.row.original)} tooltip="Edit" Icon={PencilAltIcon} />
          <ActionButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleDeleteClick(info.row.original.id)
            }}
            tooltip="Delete"
            Icon={TrashIcon}
          />
        </ActionButtonContainer>
      )
    })
  ]

  return <>
    {/* @ts-ignore */}
    <ConfirmDelete
      itemName="Contact"
      open={confirmDeleteOpen}
      setOpen={setConfirmDeleteOpen}
      onConfirm={handleConfirmDelete}
    />
    <CreateUpdatePersonFlyout
      open={createUpdatePersonOpen}
      setOpen={setCreateUpdatePersonOpen}
      person={editPerson}
    />
    <ImportPeopleModal
      open={importOpen}
      setOpen={setImportOpen}
      agencyId={agency?.id!}
    />
    <div className="px-4 sm:px-6 lg:px-8">
      <PageHeader title="Contact List" description="View and manage contacts here" />
      <div className="flex items-center justify-between">
        <div className="w-1/2 flex items-end space-x-4">
          <div className="w-1/2">
            <SearchInput placeholder="Search by Name, Email or Postcode" onSearchTermChange={(e) => setSearchTerm(e.currentTarget.value)} />
          </div>
          {marketingGroups.isLoading ?
            <div>Loading...</div>
            :
            <StyledSelectInput
              label="Filter by Group"
              classes="w-1/2"
              options={[{ label: "All", value: "-1" }, ...marketingGroups.data?.data.map(mg => ({ value: mg.id, label: mg.name })) || []]}
              watch={watch}
              setValue={setValue}
              register={register}
              registerName="selectedGroup"
              value={selectedGroup}
              onChange={(v) => setSelectedGroup(v)}
            />
          }
        </div>
        <div className="space-x-2">
          <ButtonInput
            label="Import Contacts"
            isSubmit={false}
            onClick={() => {
              setImportOpen(true);
            }}
            classes=""
            children={undefined}
          />
          <ButtonInput
            label="Add New Contact"
            isSubmit={false}
            onClick={() => {
              setCreateUpdatePersonOpen(true);
            }}
            classes=""
            children={undefined}
          />
        </div>
      </div>
      <Table<IPerson>
        url={`/person/agency/${agency?.id}`}
        queryKey={peopleQueryKeys.all(agency?.id)}
        queryString={{ marketingGroupIds: selectedGroup !== `-1` ? selectedGroup : undefined }}
        columns={cols}
        searchTerm={debouncedSearchTerm}
        onRowClick={(row) => navigate(`${row.original.id}`)}
      />
    </div>
  </>;
}